import { useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { Checkbox } from 'components/Checkbox';
import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { Input } from 'components/input/Input';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import {
  type AccessorialLookupItem,
  type EquipmentMappingItem,
  type LookupItem,
} from 'lib/api/getEquipmentLookupsGlobalTranz';
import { AccessorialItem, NormalizedLoad } from 'types/Load';
import { Undef } from 'types/UtilityTypes';

import { TARP_SIZE_OPTIONS, getAccessorialDisplayValue } from './constants';

interface AccessorialsFormProps {
  formMethods: UseFormReturn<NormalizedLoad>;
  tmsID?: number;
  equipmentMapping: EquipmentMappingItem[];
  equipmentTypes: LookupItem[];
  /** When provided (from bootstrap), used instead of fetching */
  accessorials?: AccessorialLookupItem[];
  displayFields?: LookupItem[];
  /** When true, bootstrap is in flight (initial load or refresh); show loading state */
  isBootstrapLoading?: boolean;
}

// Default accessorials (location-level, accessorialTypeId 2): always shown for every equipment type.
// Origin Liftgate, Destination Liftgate, Origin Lumper, Destination Lumper
const DEFAULT_ACCESSORIAL_IDS = [5014, 5015, 5017, 5019] as const;

// Display names: use getAccessorialDisplayValue(key) from constants (key = API displayText for main and sub fields)

// Helper function to get accessorial IDs for a given equipment type
const getAccessorialIDsForEquipment = (
  equipmentType: string,
  equipmentTypes: LookupItem[],
  equipmentMapping: EquipmentMappingItem[]
): number[] => {
  const equipmentTypeItem = equipmentTypes.find(
    (t) => t.displayText === equipmentType
  );
  if (!equipmentTypeItem) return [];

  if (equipmentTypeItem.id === 25 || equipmentTypeItem.id === 26) {
    return []; // Intermodal (25) / Reefer Intermodal (26): only 4 defaults, no mapping
  }

  const mappingItem = equipmentMapping.find(
    (m) => m.id === equipmentTypeItem.id
  );
  if (!mappingItem) return [];

  const accessorialField = mappingItem.displayFields.find((f) => f.id === 8);
  if (!accessorialField || !accessorialField.values) return [];

  return accessorialField.values.map((v) => v.id);
};

// Helper function to get dependent display fields for an accessorial
const getDependentDisplayFieldsForAccessorial = (
  accessorialID: number,
  equipmentType: string,
  equipmentTypes: LookupItem[],
  equipmentMapping: EquipmentMappingItem[]
): number[] => {
  const equipmentTypeItem = equipmentTypes.find(
    (t) => t.displayText === equipmentType
  );
  if (!equipmentTypeItem) return [];

  const mappingItem = equipmentMapping.find(
    (m) => m.id === equipmentTypeItem.id
  );
  if (!mappingItem) return [];

  const accessorialField = mappingItem.displayFields.find((f) => f.id === 8);
  if (!accessorialField || !accessorialField.values) return [];

  const accessorialValue = accessorialField.values.find(
    (v) => v.id === accessorialID
  );
  if (!accessorialValue || !accessorialValue.displayFields) return [];

  return accessorialValue.displayFields.map((df) => df.id);
};

export function AccessorialsForm({
  formMethods,
  tmsID: _tmsID,
  equipmentMapping,
  equipmentTypes,
  accessorials: providedAccessorials,
  displayFields: providedDisplayFields,
  isBootstrapLoading = false,
}: AccessorialsFormProps) {
  const { setValue, watch, getValues } = formMethods;

  const [allAccessorials, setAllAccessorials] = useState<
    AccessorialLookupItem[]
  >(providedAccessorials ?? []);
  const [displayFields, setDisplayFields] = useState<LookupItem[]>(
    providedDisplayFields ?? []
  );

  const watchedTransportType = watch('specifications.transportType');
  const watchedAccessorials = watch('accessorials') || [];

  useEffect(() => {
    if (providedAccessorials) {
      setAllAccessorials(providedAccessorials);
    }
  }, [providedAccessorials]);

  useEffect(() => {
    if (providedDisplayFields) {
      setDisplayFields(providedDisplayFields);
    }
  }, [providedDisplayFields]);

  // Accessorials and display fields are provided by parent (bootstrap)

  // Filter accessorials based on selected equipment type
  const filteredAccessorials = useMemo(() => {
    if (
      !watchedTransportType ||
      !equipmentTypes.length ||
      !equipmentMapping.length
    ) {
      return [];
    }

    const equipmentAccessorialIDs = getAccessorialIDsForEquipment(
      watchedTransportType,
      equipmentTypes,
      equipmentMapping
    );

    // Combine equipment-specific accessorials with default accessorials, removing duplicates
    const allAccessorialIDs = new Set([
      ...equipmentAccessorialIDs,
      ...DEFAULT_ACCESSORIAL_IDS,
    ]);

    return allAccessorials.filter((acc) =>
      allAccessorialIDs.has(acc.accessorialId)
    );
  }, [watchedTransportType, equipmentTypes, equipmentMapping, allAccessorials]);

  // Filter out invalid accessorials when equipment type changes
  useEffect(() => {
    if (
      !watchedTransportType ||
      !equipmentTypes.length ||
      !equipmentMapping.length ||
      !allAccessorials.length
    ) {
      return;
    }

    const currentAccessorials = getValues('accessorials') || [];
    if (currentAccessorials.length === 0) {
      return;
    }

    const equipmentAccessorialIDs = getAccessorialIDsForEquipment(
      watchedTransportType,
      equipmentTypes,
      equipmentMapping
    );

    const validAccessorialIDs = new Set([
      ...equipmentAccessorialIDs,
      ...DEFAULT_ACCESSORIAL_IDS,
    ]);

    const validAccessorials = currentAccessorials.filter((acc) =>
      validAccessorialIDs.has(acc.id)
    );

    if (validAccessorials.length !== currentAccessorials.length) {
      setValue('accessorials', validAccessorials, { shouldDirty: true });
    }
  }, [
    watchedTransportType,
    equipmentTypes,
    equipmentMapping,
    allAccessorials,
    getValues,
    setValue,
  ]);

  // Get display field label by id (raw API displayText); use getAccessorialDisplayValue() for display
  const getDisplayFieldLabel = (displayFieldId: number): string => {
    const field = displayFields.find((df) => df.id === displayFieldId);
    return field?.displayText || `Field ${displayFieldId}`;
  };

  // Check if accessorial is selected
  const isAccessorialSelected = (accessorialID: number): boolean => {
    return watchedAccessorials.some((acc) => acc.id === accessorialID);
  };

  // Get selected accessorial
  const getSelectedAccessorial = (
    accessorialID: number
  ): Undef<AccessorialItem> => {
    return watchedAccessorials.find((acc) => acc.id === accessorialID);
  };

  // Handle accessorial checkbox change
  const handleAccessorialChange = (
    accessorialID: number,
    checked: boolean | 'indeterminate'
  ) => {
    const isChecked = checked === true;
    // Use getValues() to get the most up-to-date value instead of watchedAccessorials
    const currentAccessorials = getValues('accessorials') || [];
    if (isChecked) {
      // Add accessorial if not already present
      const alreadyExists = currentAccessorials.some(
        (acc) => acc.id === accessorialID
      );
      if (!alreadyExists) {
        const accessorial = allAccessorials.find(
          (acc) => acc.accessorialId === accessorialID
        );
        if (accessorial) {
          setValue(
            'accessorials',
            [
              ...currentAccessorials,
              { id: accessorialID, displayText: accessorial.displayText },
            ],
            { shouldDirty: true }
          );
        }
      }
    } else {
      // Remove accessorial
      setValue(
        'accessorials',
        currentAccessorials.filter((acc) => acc.id !== accessorialID),
        { shouldDirty: true }
      );
    }
  };

  // Handle dependent field value change
  const handleDependentFieldChange = (
    accessorialID: number,
    displayFieldId: number,
    value: string | number
  ) => {
    const currentAccessorials = getValues('accessorials') || [];
    const accessorialIndex = currentAccessorials.findIndex(
      (acc) => acc.id === accessorialID
    );

    if (accessorialIndex === -1) return;

    const accessorial = currentAccessorials[accessorialIndex];
    const dependentFields = getDependentDisplayFieldsForAccessorial(
      accessorialID,
      watchedTransportType || '',
      equipmentTypes,
      equipmentMapping
    );

    let newValue: string | number | Record<number, string | number>;
    if (dependentFields.length === 1) {
      // Single dependent field - store as simple value
      newValue = value;
    } else {
      // Multiple dependent fields - store as object
      const currentValue = accessorial.value;
      const valueObj =
        typeof currentValue === 'object' && currentValue !== null
          ? { ...(currentValue as Record<number, string | number>) }
          : {};
      valueObj[displayFieldId] = value;
      newValue = valueObj;
    }

    const updatedAccessorials = [...currentAccessorials];
    updatedAccessorials[accessorialIndex] = {
      ...accessorial,
      value: newValue,
    };

    setValue('accessorials', updatedAccessorials, { shouldDirty: true });
  };

  // Get dependent field value
  const getDependentFieldValue = (
    accessorialID: number,
    displayFieldId: number
  ): Undef<string | number> => {
    const accessorial = getSelectedAccessorial(accessorialID);
    if (!accessorial || !accessorial.value) return undefined;

    const dependentFields = getDependentDisplayFieldsForAccessorial(
      accessorialID,
      watchedTransportType || '',
      equipmentTypes,
      equipmentMapping
    );

    if (dependentFields.length === 1) {
      return typeof accessorial.value === 'object' && accessorial.value !== null
        ? undefined
        : (accessorial.value as string | number);
    } else {
      const valueObj = accessorial.value as Record<number, string | number>;
      return valueObj[displayFieldId];
    }
  };

  if (isBootstrapLoading) {
    return <Typography variant='body-sm'>Loading accessorials...</Typography>;
  }

  if (!watchedTransportType) {
    return (
      <Typography variant='body-sm' className='text-neutral-500'>
        Please select an equipment type to view accessorials
      </Typography>
    );
  }

  if (filteredAccessorials.length === 0) {
    return (
      <Typography variant='body-sm' className='text-neutral-500'>
        No accessorials available for the selected equipment type
      </Typography>
    );
  }

  return (
    <Grid cols='4' gap='sm' className='mx-0 w-full'>
      <div className='col-span-4'>
        <Typography
          variant='subtitle2'
          className='text-neutral-700 font-medium mb-4'
        >
          Accessorials and Special Services
        </Typography>
      </div>

      {filteredAccessorials.map((accessorial) => {
        const accessorialID = accessorial.accessorialId;
        const isSelected = isAccessorialSelected(accessorialID);
        const dependentFields = getDependentDisplayFieldsForAccessorial(
          accessorialID,
          watchedTransportType,
          equipmentTypes,
          equipmentMapping
        );

        return (
          <div key={accessorialID} className='col-span-2 space-y-2'>
            <Flex align='center' gap='sm'>
              <Checkbox
                onCheckedChange={(checked) => {
                  handleAccessorialChange(accessorialID, checked);
                }}
                checked={isSelected ? true : false}
              />
              <label
                htmlFor={`accessorial-${accessorialID}`}
                className='leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-neutral-500'
              >
                {getAccessorialDisplayValue(accessorial.displayText)}
              </label>
            </Flex>

            {/* Show dependent fields when accessorial is checked */}
            {isSelected &&
              dependentFields.map((displayFieldId) => {
                const rawLabel = getDisplayFieldLabel(displayFieldId);
                const isTarpSize = rawLabel === 'Tarp Size';
                return (
                  <div key={displayFieldId} className='ml-6 mt-3'>
                    <Label
                      hideAIHint={true}
                      name={`accessorial-${accessorialID}-${displayFieldId}`}
                      required={false}
                    >
                      {getAccessorialDisplayValue(rawLabel)}
                    </Label>
                    {isTarpSize ? (
                      <Select
                        className='text-neutral-500'
                        value={(() => {
                          const raw = getDependentFieldValue(
                            accessorialID,
                            displayFieldId
                          );
                          // Legacy data may store 0 (number); TARP_SIZE_OPTIONS are strings
                          if (raw == null || raw === '' || raw === 0) {
                            return 'Not Specified';
                          }
                          const s = String(raw);
                          return (
                            TARP_SIZE_OPTIONS as readonly string[]
                          ).includes(s)
                            ? s
                            : 'Not Specified';
                        })()}
                        onChange={(value) => {
                          if (typeof value !== 'string') {
                            return;
                          }

                          handleDependentFieldChange(
                            accessorialID,
                            displayFieldId,
                            value
                          );
                        }}
                        options={TARP_SIZE_OPTIONS.map((option) => ({
                          value: option,
                          label: option,
                        }))}
                      />
                    ) : (
                      <Input
                        type='number'
                        className='mt-1 text-neutral-500'
                        value={
                          getDependentFieldValue(
                            accessorialID,
                            displayFieldId
                          ) ?? ''
                        }
                        onChange={(e) => {
                          const value =
                            e.target.value === ''
                              ? 0
                              : parseFloat(e.target.value) || 0;
                          handleDependentFieldChange(
                            accessorialID,
                            displayFieldId,
                            value
                          );
                        }}
                        placeholder='Enter value'
                        min={0}
                      />
                    )}
                  </div>
                );
              })}
          </div>
        );
      })}
    </Grid>
  );
}
